import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/modules/common/api/index.ts';
import {
  BULK_REPORT_CHILDREN_QUERY_KEY,
  REPORT_LIST_QUERY_KEY,
} from '@/modules/common/queries/reports.ts';
import { queryClient } from '@/modules/common/query.ts';
import {
  PENDING_BULK_REPORT_CHANGE_EVENT,
  clearPendingBulkReport,
  getPendingBulkReport,
} from '@/modules/common/utils/pending-bulk-report.ts';
import { openInNewTab } from 'ui/utils';
import { GetReportListResponse, ReportStatus } from 'api';

const REPORT_POLL_INTERVAL_MS = 10000;
const REPORT_POLL_TIMEOUT_MS = 10 * 60 * 1000;

export const BulkReportDownloadController = () => {
  const [pendingReport, setPendingReport] = useState(() => getPendingBulkReport());

  useEffect(() => {
    const handlePendingReportChange = () => setPendingReport(getPendingBulkReport());

    window.addEventListener(PENDING_BULK_REPORT_CHANGE_EVENT, handlePendingReportChange);
    return () => {
      window.removeEventListener(PENDING_BULK_REPORT_CHANGE_EVENT, handlePendingReportChange);
    };
  }, []);

  const { data } = useQuery<GetReportListResponse>({
    queryKey: ['vestra/pending-bulk-report', pendingReport?.id],
    queryFn: async () => {
      if (!pendingReport) throw new Error('Pending bulk report is not set');

      const response = await api.v1.getReportsListV1SrReportsGet(
        {
          page: 1,
          page_size: 45,
          register_id: pendingReport.registerId,
        },
        {
          headers: { 'xRegister-ID': pendingReport.registerId },
        },
      );
      return response.data;
    },
    enabled: !!pendingReport,
    refetchInterval: pendingReport ? REPORT_POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (!pendingReport) return;

    const remainingTime = REPORT_POLL_TIMEOUT_MS - (Date.now() - pendingReport.startedAt);
    const timeoutId = window.setTimeout(() => {
      toast.error(
        'Report generation is taking longer than expected. Check the Reports page later.',
      );
      clearPendingBulkReport();
    }, Math.max(remainingTime, 0));

    return () => window.clearTimeout(timeoutId);
  }, [pendingReport]);

  useEffect(() => {
    if (!pendingReport) return;

    const report = data?.data.find(item => item.id === pendingReport.id);
    if (!report || report.status === ReportStatus.Generating) return;

    void (async () => {
      if (report.status === ReportStatus.Completed && report.file_storage_item_url) {
        openInNewTab(report.file_storage_item_url);
        await queryClient.invalidateQueries([
          BULK_REPORT_CHILDREN_QUERY_KEY,
          pendingReport.id,
          pendingReport.registerId,
        ]);
      } else if (report.status === ReportStatus.Completed) {
        toast.error('Report was generated without a download URL');
        await queryClient.invalidateQueries([
          BULK_REPORT_CHILDREN_QUERY_KEY,
          pendingReport.id,
          pendingReport.registerId,
        ]);
      } else if (report.status === ReportStatus.Failed) {
        toast.error('Report generation failed');
      }

      clearPendingBulkReport();
      await queryClient.invalidateQueries([REPORT_LIST_QUERY_KEY]);
    })();
  }, [data?.data, pendingReport]);

  return null;
};
